import { UseQueryResult, useQuery } from '@tanstack/react-query'
import { getContentfulClient } from '../../contentful/contentfulClient'
import { Asset, Entry, EntryCollection } from 'contentful'
import {
  AnyBlock,
  IArticleFields,
  IPodcastEpisode,
  IPortalGeneralPageFields,
  ISpecialistFields,
  ITrainingFields,
  IWebinarFields,
} from '@estercare/ester-shared'
import { useSelectUserLocale } from '../../state/selectors'

export const useGetContentWithSwedishFallback = <T>(
  primaryQuery: UseQueryResult<EntryCollection<T>, Error>,
  fallbackQuery: (slug: string, options?: { enabled?: boolean }) => UseQueryResult<EntryCollection<T>, Error>,
  slug: string
): {
  data: Entry<T> | undefined
  isLoading: boolean
  error: Error | null
} => {
  const shouldUseSwedishSlug =
    !primaryQuery.isLoading &&
    !primaryQuery.error &&
    (!primaryQuery.data?.items || primaryQuery.data.items.length === 0)

  const contentFromSwedishSlug = fallbackQuery(slug, { enabled: shouldUseSwedishSlug })

  const content =
    primaryQuery.data?.items[0] || (shouldUseSwedishSlug ? contentFromSwedishSlug.data?.items[0] : undefined)

  const isLoading = primaryQuery.isLoading || (shouldUseSwedishSlug && contentFromSwedishSlug.isLoading)

  const error = primaryQuery.error || contentFromSwedishSlug.error

  return { data: content, isLoading, error }
}

export const useGetGeneralPage = (slug: string): UseQueryResult<EntryCollection<IPortalGeneralPageFields>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['portalGeneralPage', slug, locale],
    queryFn: async () => {
      const params = {
        content_type: 'portalGeneralPage',
        'fields.slug': slug,
        include: 4,
        ...(locale && { locale }),
      }
      return await getContentfulClient().getEntries<IPortalGeneralPageFields>(params)
    },
  })
}

export const useGetServicePages = (): UseQueryResult<EntryCollection<IPortalGeneralPageFields>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['portalGeneralPage', 'AllServices', locale],
    queryFn: async () => {
      const params = {
        content_type: 'portalGeneralPage',
        'fields.pageType': 'SERVICE PAGE',
        ...(locale && { locale }),
      }
      return await getContentfulClient().getEntries<AnyBlock>(params)
    },
  })
}

export const useGetEntryById = (id: string): UseQueryResult<Entry<AnyBlock>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['entry', id, locale],
    queryFn: async () => {
      const params = {
        include: 4,
        ...(locale && { locale }),
      }
      return await getContentfulClient().getEntry<AnyBlock>(id, params)
    },
  })
}

export const useGetAssetById = (id: string): UseQueryResult<Asset, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['asset', id, locale],
    queryFn: async () => {
      const params = {
        ...(locale && { locale }),
      }
      return await getContentfulClient().getAsset(id, params)
    },
    enabled: !!id,
  })
}

export const useGetPortalGeneralPageById = (id: string): UseQueryResult<Entry<IPortalGeneralPageFields>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['portalGeneralPageById', id, locale],
    queryFn: async () => {
      const params = {
        include: 4,
        ...(locale && { locale }),
      }
      return await getContentfulClient().getEntry<IPortalGeneralPageFields>(id, params)
    },
  })
}

export const useGetEntriesByContentType = <T>(contentType: string): UseQueryResult<EntryCollection<T>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['entries', contentType, locale],
    queryFn: async () => {
      const params = {
        content_type: contentType,
        include: 4,
        limit: 1000,
        ...(locale && { locale }),
      }
      return await getContentfulClient().getEntries<T[]>(params)
    },
  })
}

export const useGetArticleBySlug = (slug: string): UseQueryResult<EntryCollection<IArticleFields>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['article', slug, locale],
    queryFn: async () => {
      const params = {
        content_type: 'article',
        'fields.slug': slug,
        ...(locale && { locale }),
      }
      return await getContentfulClient().getEntries<IArticleFields>(params)
    },
  })
}

//Temporary helper function to get content by Swedish slug in a specific locale
const getLocalisedContentBySwedishSlug = async <T>(
  contentType: 'article' | 'podcastEpisode',
  slug: string,
  locale: string | undefined
): Promise<EntryCollection<T>> => {
  const swedishEntry = await getContentfulClient().getEntries<T>({
    content_type: contentType,
    'fields.slug': slug,
  })

  if (!swedishEntry.items || swedishEntry.items.length === 0 || !swedishEntry.items[0]) {
    return {
      items: [],
      total: 0,
      skip: 0,
      limit: 0,
      stringifySafe: () => '',
      toPlainObject: () => ({ items: [], total: 0, skip: 0, limit: 0 }),
    } as EntryCollection<T>
  }

  const entryId = swedishEntry.items[0].sys.id

  const localisedEntry = await getContentfulClient().getEntries<T>({
    content_type: contentType,
    'sys.id': entryId,
    include: 4,
    locale,
  })

  return localisedEntry
}

//This is a temporary solution until we have the new healthplan without Swedish slugs
export const useGetLocalisedArticleBySwedishSlug = (
  slug: string,
  options?: { enabled?: boolean }
): UseQueryResult<EntryCollection<IArticleFields>, Error> => {
  const locale = useSelectUserLocale()

  return useQuery({
    queryKey: ['article', slug, 'swedishSlug', locale],
    queryFn: async () => {
      return await getLocalisedContentBySwedishSlug<IArticleFields>('article', slug, locale)
    },
    enabled: !!slug && options?.enabled !== false,
  })
}

export const useGetPodcastBySlug = (slug: string): UseQueryResult<EntryCollection<IPodcastEpisode>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['contentfulEntry', 'podcastEpisode', slug, locale],
    queryFn: async () => {
      const params = {
        content_type: 'podcastEpisode',
        'fields.slug': slug,
        limit: 1,
        ...(locale && { locale }),
      }
      const response = await getContentfulClient().getEntries<AnyBlock>(params)
      return response
    },
  })
}

//This is a temporary solution until we have the new healthplan without Swedish slugs
export const useGetLocalisedPodcastBySwedishSlug = (
  slug: string,
  options?: { enabled?: boolean }
): UseQueryResult<EntryCollection<IPodcastEpisode>, Error> => {
  const locale = useSelectUserLocale()

  return useQuery({
    queryKey: ['podcast', slug, 'swedishSlug', locale],
    queryFn: async () => {
      return await getLocalisedContentBySwedishSlug<IPodcastEpisode>('podcastEpisode', slug, locale)
    },
    enabled: !!slug && options?.enabled !== false,
  })
}

export const useGetWebinarBySlug = (slug: string): UseQueryResult<EntryCollection<IWebinarFields>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['contentfulEntry', 'webinar', slug, locale],
    queryFn: async () => {
      const params = {
        content_type: 'webinar',
        'fields.slug': slug,
        limit: 1,
        include: 4,
        ...(locale && { locale }),
      }
      return await getContentfulClient().getEntries<IWebinarFields>(params)
    },
  })
}

export const useGetTrainingBySlug = (slug: string): UseQueryResult<EntryCollection<ITrainingFields>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['contentfulEntry', 'training', slug, locale],
    queryFn: async () => {
      const params = {
        content_type: 'training',
        'fields.slug': slug,
        limit: 1,
        ...(locale && { locale }),
      }
      const response = await getContentfulClient().getEntries<ITrainingFields>(params)
      return response
    },
  })
}

export const useGetSpecialistById = (
  id: string,
  options?: { enabled?: boolean }
): UseQueryResult<EntryCollection<ISpecialistFields>, Error> => {
  const locale = useSelectUserLocale()
  return useQuery({
    queryKey: ['contentfulEntry', 'specialist', id, locale],
    queryFn: async () => {
      const params = {
        content_type: 'specialist',
        'fields.specialistId': id,
        limit: 1,
        ...(locale && { locale }),
      }
      const response = await getContentfulClient().getEntries<ISpecialistFields>(params)
      return response
    },
    enabled: options?.enabled !== false && !!id,
  })
}
